import React, { useMemo, useState, useCallback } from 'react';
import { Menu, MenuItem, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import metaDataImage from '../../images/metadata.png';
import { useWebSocket } from '../WebSocketContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LINKED_PROJECT_PREFIX } from '../../constants/projectLink';

import ActionsLogsDialog from '../ActionsLogsDialog';

// Theme colors - matching Sidebar
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';

const lightPurple = '#f0f0ff';

export const isChildTimeEvent = (timeevent) => {
  const parents = timeevent?.parents;
  return Array.isArray(parents) ? parents.length > 0 : Boolean(parents);
};

export const isTimeEventDeleteDisabled = (timeevent) => (
  !isChildTimeEvent(timeevent)
  && Boolean(timeevent?.isExist && !timeevent?.isDeletable)
);

const TimeEventMenu = React.memo(({
  anchorEl,
  handleMenuClose,
  handleMetaClick,
  handleConfirmEvent,
  onToggleHide,
  onToggleRenalyze,
  timeevent,
  canManageProject = false
}) => {
  const { t } = useTranslation();
  const isLinkedProjectEvent = typeof timeevent?.eventsource === 'string' && timeevent.eventsource.startsWith(LINKED_PROJECT_PREFIX);
  const isDeleteDisabled = isTimeEventDeleteDisabled(timeevent);
  // Keep confirmation unavailable while either ingestion or post-processing
  // is active. The backend enforces the same rule, but the menu should reflect
  // it immediately for the user.
  const isConfirmationDisabled = Number(timeevent?.eventstatus) === 1
    || timeevent?.postprocessing_state === 'processing';

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openActionsLogsDialog, setOpenActionsLogsDialog] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const { sendMessage, ws } = useWebSocket();
  const navigate = useNavigate();

  const handleReanalyzeClick = () => {
    setOpenDialog(true);
  };
  const handleConfirmClick = () => {
    setOpenConfirmDialog(true);
  };
  const handleDeleteClick = () => {
    setDeleteError(''); // Clear any previous errors
    setOpenDeleteDialog(true);
  };

  const handleActionsLogsClick = () => {
    setOpenActionsLogsDialog(true);
  };

  const handleDeleteDialogClose = (confirm) => {
    setOpenDeleteDialog(false);
    if (confirm) {
      sendMessage({ command: 'deleteTimeEvent', id: timeevent.id });
    }
  };
  const handleConfirmDialogClose = (confirm) => {
    setOpenConfirmDialog(false);
    if (confirm && !isConfirmationDisabled) {
      handleConfirmEvent();
    }
  };

  const handleDialogClose = (confirm) => {
    setOpenDialog(false);
    if (confirm) {
      onToggleRenalyze();
    }
  };

  const handleOpenLocation = useCallback(() => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ command: 'openfolderoftimeevent', id: timeevent.id }));
    }
  }, [ws, timeevent.id]);
  const handleOpenLinkedProject = useCallback(() => {
    if (!isLinkedProjectEvent) return;
    const linkedIdRaw = String(timeevent?.eventsource || '').replace(LINKED_PROJECT_PREFIX, '');
    const linkedId = parseInt(linkedIdRaw, 10);
    if (linkedId > 0) {
      navigate(`/case/${linkedId}`);
    }
  }, [isLinkedProjectEvent, timeevent?.eventsource, navigate]);
  const menuItems = useMemo(() => {
    const isSoundFile = timeevent.eventsource && (timeevent.eventsource.endsWith('.wav') || timeevent.eventsource.endsWith('.m4a') || timeevent.eventsource.endsWith('.mp3'));
    const items = [{
      onClick: handleConfirmClick,
      icon: <CheckCircleIcon style={{ marginRight: '8px', color: primaryColor }} />,
      text: timeevent.isConfirmed === 1 ? "Unconfirm Event" : "Confirm Event",
      disabled: isConfirmationDisabled,
      tooltip: isConfirmationDisabled
        ? t('timeevent.postprocessing_status_tooltip')
        : (timeevent.isConfirmed === 1
          ? "Remove confirmation status from this event"
          : "Mark this event as confirmed and verified"),
    },
    {
      onClick: handleMetaClick,
      icon: (
        <img
          src={metaDataImage}
          alt="Metadata"
          width="20"
          height="20"
          style={{ marginRight: '8px' }}
        />
      ),
      text: isSoundFile ? "View Transcription" : "View Meta",
      tooltip: isSoundFile
        ? "View the audio transcription and metadata"
        : "View detailed metadata and extracted information",
    },
    {
      onClick: onToggleHide,
      icon: timeevent.hide ? (
        <VisibilityIcon style={{ marginRight: '8px', color: primaryColor }} />
      ) : (
        <VisibilityOffIcon style={{ marginRight: '8px', color: primaryColor }} />
      ),
      text: timeevent.hide ? "Show Event" : "Hide Event",
      tooltip: timeevent.hide
        ? "Make this event visible in the timeline"
        : "Hide this event from the main timeline view",
    }];

    if (isLinkedProjectEvent) {
      items.push({
        onClick: handleOpenLinkedProject,
        icon: <OpenInNewIcon style={{ marginRight: '8px', color: primaryColor }} />,
        text: "Open Linked Project",
        tooltip: "Open the linked child project",
      });
    } else {
      items.push({
        onClick: handleReanalyzeClick,
        icon: <RestartAltIcon style={{ marginRight: '8px', color: primaryColor }} />,
        text: "Reanalyze",
        tooltip: "Re-process this event with AI (will reset all manual edits)",
      });
    }

    items.push(
    {
      onClick: handleActionsLogsClick,
      icon: <HistoryIcon style={{ marginRight: '8px', color: primaryColor }} />,
      text: "Event Logs",
      tooltip: "View the logs of all actions performed on this time event",
    },
    ...(!isLinkedProjectEvent ? [{
      onClick: handleOpenLocation,
      icon: <LocationOnIcon style={{ marginRight: '8px', color: primaryColor }} />,
      text: "Open Location",
      tooltip: "Open the file location in your system file explorer",
    }] : []),
    {
      onClick: handleDeleteClick,
      // compute enabled state once and use it for both `disabled` and icon color
      icon: (() => {
        const isDeleteEnabled = !isDeleteDisabled;
        return <DeleteIcon style={{ marginRight: '8px', color: isDeleteEnabled ? '#d32f2f' : '#ccc' }} />;
      })(),
      text: isLinkedProjectEvent ? "Unlink Project" : "Delete Event",
      disabled: isDeleteDisabled,
      tooltip: isDeleteDisabled
        ? "File is outside upload folder and cannot be deleted"
        : (isLinkedProjectEvent
          ? "Remove the linked project from this timeline"
          : "Permanently delete this event from the timeline"),
    }
    );

    return items;
  }, [handleMetaClick, onToggleHide, handleOpenLocation, timeevent.hide, timeevent.eventsource, timeevent.isConfirmed, isDeleteDisabled, isConfirmationDisabled, isLinkedProjectEvent, handleOpenLinkedProject, t]);

  // Filter menu items for view_only users
  const visibleMenuItems = useMemo(() => {
    if (canManageProject) {
      return menuItems;
    }
    // For view_only users, only show: View Meta, Event Logs, Open Location
    return menuItems.filter(item =>
      item.text === 'View Meta' ||
      item.text === 'View Transcription' ||
      item.text === 'Event Logs' ||
      item.text === 'Open Location' ||
      item.text === 'Open Linked Project'
    );
  }, [menuItems, canManageProject]);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: '8px',
            padding: '4px 0',
            minWidth: '180px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            '& .MuiList-root': {
              padding: '4px 0',
            },
          }
        }}      >        {visibleMenuItems.map((item, index) => {
          const menuItem = (
            <MenuItem
              key={item.text}
              onClick={() => {
                if (!item.disabled) {
                  item.onClick();
                  handleMenuClose();
                }
              }}
              disabled={item.disabled}
              sx={{
                padding: '8px 16px',
                fontSize: '0.875rem',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: item.disabled ? 'transparent' : lightPurple,
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }
              }}
            >
              {item.icon}
              {item.text}
            </MenuItem>
          );          // Wrap with tooltip if tooltip text exists
          if (item.tooltip) {
            return (
              <Tooltip key={item.text} title={item.tooltip} placement="left" arrow>
                <span>{menuItem}</span>
              </Tooltip>
            );
          }

          return menuItem;
        })}
      </Menu>
      <Dialog
        open={openDialog}
        onClose={() => handleDialogClose(false)}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            padding: '8px',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          color: '#d32f2f',
          fontWeight: 'bold',
          fontSize: '1.2rem',
        }}>
          <ErrorOutlineIcon style={{ color: '#d32f2f', marginRight: '8px' }} />
          Careful
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#333' }}>
            Reanalyzing with AI will reset this event and erase all your edits.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button
            onClick={() => handleDialogClose(false)}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDialogClose(true)}
            variant="contained"
            autoFocus
            sx={{
              backgroundColor: primaryColor,
              '&:hover': {
                backgroundColor: hoverColor,
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openConfirmDialog}
        onClose={() => handleConfirmDialogClose(false)}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            padding: '8px',
          }
        }}
      >        <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        color: primaryColor,
        fontWeight: 'bold',
        fontSize: '1.2rem',
      }}>
          <CheckCircleIcon style={{ color: primaryColor, marginRight: '8px' }} />
          {timeevent.isConfirmed === 1 ? "Unconfirm Event" : "Confirm Event"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#333' }}>
            {timeevent.isConfirmed === 1
              ? "Are you sure you want to unconfirm this event? This will remove the confirmation status."
              : "Are you sure you want to confirm this event? This action marks the event as verified."
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button
            onClick={() => handleConfirmDialogClose(false)}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>          <Button
            onClick={() => handleConfirmDialogClose(true)}
            variant="contained"
            autoFocus
            disabled={isConfirmationDisabled}
            sx={{
              backgroundColor: primaryColor,
              '&:hover': {
                backgroundColor: hoverColor,
              }
            }}
          >
            {timeevent.isConfirmed === 1 ? "Unconfirm" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteDialog}
        onClose={() => handleDeleteDialogClose(false)}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            padding: '8px',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          color: '#d32f2f',
          fontWeight: 'bold',
          fontSize: '1.2rem',
        }}>
          <DeleteIcon style={{ color: '#d32f2f', marginRight: '8px' }} />
          {isLinkedProjectEvent ? "Unlink Project" : "Delete Event"}
        </DialogTitle>        <DialogContent>
          <DialogContentText sx={{ color: '#333' }}>
            {deleteError ? (
              deleteError
            ) : (
              isLinkedProjectEvent ? (
                "Are you sure you want to unlink this project from the master timeline? This does not delete the child project."
              ) : timeevent.fileExist && !isChildTimeEvent(timeevent) ?
                <>
                  Are you sure you want to delete this event? <br />
                  <b>Warning: The file will be permanently deleted from the disk.</b> <br />
                  This action cannot be undone.
                </>
                :
                "Are you sure you want to delete this event? This action cannot be undone."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button
            onClick={() => handleDeleteDialogClose(false)}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            {deleteError ? 'OK' : 'Cancel'}
          </Button>
          {!deleteError && (
            <Button
              onClick={() => handleDeleteDialogClose(true)}
              variant="contained"
              autoFocus
              sx={{
                backgroundColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                }
              }}
            >
              {isLinkedProjectEvent ? "Unlink" : "Delete"}
            </Button>)}        </DialogActions>
      </Dialog>

      <ActionsLogsDialog
        open={openActionsLogsDialog}
        onClose={() => setOpenActionsLogsDialog(false)}
        title={`Event Logs - ${timeevent?.eventsource || 'Time Event'}`}
        caseid={timeevent?.caseid}
        timeevent_id={timeevent?.id}
      />
    </>
  );
});

export default TimeEventMenu;
